// src/pages/Users/Inscription/dossier/components/AdherentList.jsx

import React from "react";

import {
  FaChevronDown,
  FaChevronUp,
  FaRedo,
} from "react-icons/fa";

import DossierActionButton from "../ui/DossierActionButton";

import {
  getAdherentStatusTheme,
  getDefaultAvatar,
  getDossierProgress,
  getSeasonClasses,
  toAbsoluteUrl,
} from "../utils/dossierInscription.utils";

import AdherentDossierPanel from "./AdherentDossierPanel";

function Skeleton() {
  return (
    <div
      className="
        h-32
        animate-pulse
        rounded-2xl

        bg-slate-100

        dark:bg-slate-800
      "
    />
  );
}

export default function AdherentList({
  loading,

  adherents,

  dossiers,
  groups,

  activeSeason,
  settings,

  user,

  expandedId,
  onToggle,

  onRenew,

  refreshDossier,
  refreshAll,
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({
          length: 3,
        }).map((_, index) => (
          <Skeleton key={index} />
        ))}
      </div>
    );
  }

  if (!adherents.length) {
    return (
      <div
        className="
          rounded-[1.75rem]

          border
          border-dashed
          border-slate-300

          bg-white

          px-5
          py-10

          text-center

          dark:border-slate-700
          dark:bg-slate-900
        "
      >
        <p
          className="
            font-bold
            text-slate-900

            dark:text-white
          "
        >
          Aucun adhérent trouvé
        </p>

        <p
          className="
            mt-2
            text-sm
            text-slate-500

            dark:text-slate-400
          "
        >
          Modifiez votre recherche ou
          créez un nouvel adhérent.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {adherents.map(
        (adherent) => {
          const expanded =
            expandedId ===
            adherent.id;

          const dossier =
            dossiers[
              adherent.id
            ];

          const progress =
            getDossierProgress(
              dossier,
            );

          const status =
            getAdherentStatusTheme(
              adherent.statut_inscription,
            );

          const groupNames =
            groups[
              adherent.id
            ] || [];

          return (
            <article
              key={adherent.id}
              className={`
                min-w-0
                overflow-hidden

                rounded-[1.6rem]

                border
                border-slate-200
                border-l-4

                ${status.border}

                bg-white

                shadow-sm

                dark:border-slate-800
                dark:bg-slate-900
              `}
            >
              {/* Résumé */}

              <div className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <img
                    src={
                      toAbsoluteUrl(
                        adherent.photo_url,
                      ) ||
                      getDefaultAvatar(
                        adherent.genre,
                      )
                    }
                    alt={`${adherent.nom || ""} ${adherent.prenom || ""}`}
                    className="
                      h-14
                      w-14
                      shrink-0

                      rounded-2xl
                      object-cover

                      ring-1
                      ring-slate-200

                      dark:ring-slate-700

                      sm:h-16
                      sm:w-16
                    "
                  />

                  <div className="min-w-0 flex-1">
                    <h3
                      className="
                        break-words

                        font-black
                        leading-5

                        text-slate-950

                        dark:text-white

                        sm:text-lg
                      "
                    >
                      {String(
                        adherent.nom ||
                          "",
                      ).toUpperCase()}{" "}
                      {
                        adherent.prenom
                      }
                    </h3>

                    <div
                      className="
                        mt-2
                        flex
                        flex-wrap
                        gap-1.5
                      "
                    >
                      <span
                        className={`
                          inline-flex
                          rounded-full
                          border

                          px-2.5
                          py-1

                          text-[10px]
                          font-bold

                          ${status.badge}
                        `}
                      >
                        {adherent.statut_inscription ||
                          "Statut inconnu"}
                      </span>

                      <span
                        className={`
                          inline-flex
                          rounded-full

                          px-2.5
                          py-1

                          text-[10px]
                          font-bold

                          ${getSeasonClasses(
                            adherent.saison_active,
                            activeSeason,
                          )}
                        `}
                      >
                        Saison{" "}
                        {adherent.saison_active ||
                          "—"}
                      </span>
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <strong
                      className="
                        text-lg
                        font-black

                        text-[#013E8E]

                        dark:text-blue-300
                      "
                    >
                      {progress.percent}%
                    </strong>

                    <p
                      className="
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-wide
                        text-slate-400
                      "
                    >
                      dossier
                    </p>
                  </div>
                </div>

                {/* Groupe */}

                <div
                  className="
                    mt-4

                    rounded-xl

                    bg-slate-50

                    px-3
                    py-2.5

                    dark:bg-slate-950/50
                  "
                >
                  <p
                    className="
                      text-[9px]
                      font-black
                      uppercase
                      tracking-[0.12em]
                      text-slate-400
                    "
                  >
                    Groupe
                  </p>

                  <p
                    className="
                      mt-1

                      break-words

                      text-xs
                      font-bold

                      text-slate-700

                      dark:text-slate-300
                    "
                  >
                    {groupNames.length
                      ? groupNames.join(
                          ", ",
                        )
                      : "Non attribué"}
                  </p>
                </div>

                {/* Progress */}

                <div className="mt-3">
                  <div
                    className="
                      h-1.5
                      overflow-hidden
                      rounded-full

                      bg-slate-100

                      dark:bg-slate-800
                    "
                  >
                    <div
                      className="
                        h-full
                        rounded-full
                        bg-[#013E8E]

                        transition-all

                        dark:bg-blue-500
                      "
                      style={{
                        width: `${progress.percent}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Actions */}

                <div
                  className="
                    mt-4

                    grid
                    grid-cols-1
                    gap-2

                    min-[400px]:grid-cols-2
                  "
                >
                  <DossierActionButton
                    variant={
                      expanded
                        ? "neutral"
                        : "primary"
                    }
                    icon={
                      expanded
                        ? FaChevronUp
                        : FaChevronDown
                    }
                    onClick={() =>
                      onToggle(
                        adherent.id,
                      )
                    }
                    className="w-full"
                  >
                    {expanded
                      ? "Masquer le dossier"
                      : "Voir le dossier"}
                  </DossierActionButton>

                  {activeSeason &&
                  adherent.saison_active !==
                    activeSeason ? (
                    <DossierActionButton
                      variant="amber"
                      icon={FaRedo}
                      onClick={() =>
                        onRenew(
                          adherent,
                        )
                      }
                      className="w-full"
                    >
                      Renouveler
                    </DossierActionButton>
                  ) : null}
                </div>
              </div>

              {/* Dossier */}

              {expanded ? (
                <div
                  className="
                    border-t
                    border-slate-100

                    bg-slate-50/50

                    p-3

                    dark:border-slate-800
                    dark:bg-slate-950/30

                    sm:p-4
                  "
                >
                  {dossier === null ? (
                    <div
                      className="
                        rounded-2xl

                        border
                        border-amber-200

                        bg-amber-50

                        p-4

                        text-sm
                        text-amber-800

                        dark:border-amber-500/20
                        dark:bg-amber-500/10
                        dark:text-amber-300
                      "
                    >
                      Aucun dossier
                      d'inscription n'a encore
                      été créé pour cet
                      adhérent.
                    </div>
                  ) : (
                    <AdherentDossierPanel
                      adherent={
                        adherent
                      }
                      dossier={
                        dossier
                      }
                      user={user}
                      settings={
                        settings
                      }
                      activeSeason={
                        activeSeason
                      }
                      refreshDossier={
                        refreshDossier
                      }
                      refreshAll={
                        refreshAll
                      }
                    />
                  )}
                </div>
              ) : null}
            </article>
          );
        },
      )}
    </div>
  );
}